import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Panel,
  addEdge,
  useEdgesState,
  useNodesState,
  ReactFlowProvider,
  useReactFlow,
  ConnectionMode,
  ConnectionLineType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/formatApiError';
import {
  WORKFLOW_NODE_TYPES,
  WORKFLOW_PALETTE,
  createWorkflowNode,
} from '@/components/nodeWorkflow/WorkflowNodes';
import {
  scenesToWorkflowGraph,
  extractLayoutFromNodes,
  writeWorkflowLayout,
  readWorkflowLayout,
  patchSceneFromWorkflowNode,
  readWorkflowUserEdges,
  writeWorkflowUserEdges,
  selectSceneMediaVersion,
  mergePatchedScene,
} from '@/lib/storyWorkflowSync';

function WorkflowPalette({ onAdd, storyBound }) {
  return (
    <aside className="w-56 shrink-0 border-r border-white/10 bg-[#0b1015]/95 flex flex-col h-full">
      <div className="px-3 py-2.5 border-b border-white/10">
        <p className="text-[10px] font-mono uppercase tracking-widest text-[#9CA3AF]">Node palette</p>
        <p className="text-[10px] text-slate-500 mt-0.5">
          {storyBound
            ? 'Story scenes are already on the canvas. Extra nodes are local helpers.'
            : 'Drag onto canvas or click to add'}
        </p>
      </div>
      <ul className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {WORKFLOW_PALETTE.map((item) => {
          const Icon = item.Icon;
          return (
            <li key={item.kind}>
              <button
                type="button"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/twinnee-workflow-kind', item.kind);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onClick={() => onAdd?.(item.kind)}
                className="w-full text-left rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/20 px-2.5 py-2 cursor-grab active:cursor-grabbing transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span
                    className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: `${item.accent}22`, color: item.accent }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold text-white truncate">{item.label}</p>
                    <p className="text-[9px] text-slate-500 truncate">{item.description}</p>
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

function WorkflowCanvasInner({
  mode,
  projectId,
  title,
  scenes,
  onScenesChange,
  loading,
  onReload,
}) {
  const layout = useMemo(
    () => readWorkflowLayout(mode, projectId),
    [mode, projectId],
  );

  const derived = useMemo(
    () => scenesToWorkflowGraph(scenes, { title, layout }),
    // layout read once per project; content follows scenes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scenes, title, mode, projectId],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(derived.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(derived.edges);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const wrapperRef = useRef(null);
  const persistTimerRef = useRef(null);
  const saveTimerRef = useRef(null);
  const { screenToFlowPosition, fitView } = useReactFlow();

  // Re-hydrate when story scenes change — keep user multi-connections
  useEffect(() => {
    const savedUser = readWorkflowUserEdges(mode, projectId);
    setNodes(derived.nodes);
    setEdges(() => {
      const storyIds = new Set(derived.edges.map((e) => e.id));
      const extras = (savedUser || [])
        .filter((e) => e?.id && !storyIds.has(e.id) && e.source && e.target)
        .map((e) => ({ ...e, type: 'default' }));
      return [...derived.edges, ...extras];
    });
    const t = setTimeout(() => fitView({ padding: 0.18 }), 80);
    return () => clearTimeout(t);
  }, [derived, mode, projectId, setNodes, setEdges, fitView]);

  useEffect(
    () => () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    [],
  );

  const persistLayout = useCallback(
    (nextNodes) => {
      if (!projectId) return;
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(() => {
        writeWorkflowLayout(mode, projectId, extractLayoutFromNodes(nextNodes));
      }, 250);
    },
    [mode, projectId],
  );

  const persistUserEdges = useCallback(
    (allEdges) => {
      if (!projectId) return;
      const userEdges = (allEdges || []).filter((e) => e?.data?.userCreated);
      writeWorkflowUserEdges(mode, projectId, userEdges);
    },
    [mode, projectId],
  );

  const isValidConnection = useCallback((connection) => {
    if (!connection?.source || !connection?.target) return false;
    if (connection.source === connection.target) return false;
    return true;
  }, []);

  const onConnect = useCallback(
    (connection) => {
      setEdges((eds) => {
        const exists = eds.some(
          (e) =>
            e.source === connection.source &&
            e.target === connection.target &&
            (e.sourceHandle || null) === (connection.sourceHandle || null) &&
            (e.targetHandle || null) === (connection.targetHandle || null),
        );
        if (exists) return eds;

        const next = addEdge(
          {
            ...connection,
            id: `user_${connection.source}_${connection.sourceHandle || 'out'}_${connection.target}_${connection.targetHandle || 'in'}_${Date.now()}`,
            animated: true,
            type: 'default',
            style: { stroke: '#62FAE3', strokeWidth: 1.5 },
            data: { userCreated: true },
          },
          eds,
        );
        persistUserEdges(next);
        return next;
      });
    },
    [persistUserEdges, setEdges],
  );

  const addNodeAt = useCallback(
    (kind, position) => {
      const node = createWorkflowNode(kind, position, {
        label: `${kind} (local)`,
      });
      // Mark palette-added nodes as not story-synced helpers
      node.data = { ...node.data, synced: false, localHelper: true };
      setNodes((nds) => {
        const next = [...nds, node];
        persistLayout(next);
        return next;
      });
      setSelectedId(node.id);
    },
    [persistLayout, setNodes],
  );

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const kind = event.dataTransfer.getData('application/twinnee-workflow-kind');
      if (!kind) return;
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      addNodeAt(kind, position);
    },
    [addNodeAt, screenToFlowPosition],
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const selected = nodes.find((n) => n.id === selectedId);

  const updateSelectedText = (text) => {
    const node = nodes.find((n) => n.id === selectedId);
    if (!node) return;

    setNodes((nds) =>
      nds.map((n) => (n.id === selectedId ? { ...n, data: { ...n.data, text } } : n)),
    );

    if (!node.data?.synced || !node.data?.sceneId || node.data?.readOnly) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await patchSceneFromWorkflowNode({
          mode,
          projectId,
          nodeData: node.data,
          text,
          scenes,
        });
        // Intentionally do not call onScenesChange here — avoids remounting the
        // graph mid-edit. Scene / Editor views load the patched fields from the API.
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to sync scene'));
      } finally {
        setSaving(false);
      }
    }, 700);
  };

  const resetToStory = () => {
    const freshLayout = {};
    writeWorkflowLayout(mode, projectId, freshLayout);
    writeWorkflowUserEdges(mode, projectId, []);
    const g = scenesToWorkflowGraph(scenes, { title, layout: freshLayout });
    setNodes(g.nodes);
    setEdges(g.edges);
    setTimeout(() => fitView({ padding: 0.18 }), 50);
    toast.message('Workflow reset to current story scenes');
  };

  return (
    <div className="flex flex-1 min-h-0 w-full">
      <WorkflowPalette
        storyBound
        onAdd={(kind) =>
          addNodeAt(kind, {
            x: 180 + Math.random() * 80,
            y: 140 + Math.random() * 120,
          })
        }
      />

      <div className="flex-1 min-w-0 relative" ref={wrapperRef}>
        {loading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#070b0e]/80">
            <Loader2 className="w-6 h-6 animate-spin text-[#62FAE3]" />
          </div>
        ) : null}

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={(changes) => {
            onNodesChange(changes);
            const moved = changes.some(
              (c) => c.type === 'position' && c.dragging === false,
            );
            if (moved) {
              setNodes((nds) => {
                persistLayout(nds);
                return nds;
              });
            }
          }}
          onEdgesChange={(changes) => {
            onEdgesChange(changes);
            // Persist after removals so multi-connections stay in sync
            const removed = changes.some((c) => c.type === 'remove');
            if (removed) {
              setTimeout(() => {
                setEdges((eds) => {
                  persistUserEdges(eds);
                  return eds;
                });
              }, 0);
            }
          }}
          onConnect={onConnect}
          isValidConnection={isValidConnection}
          connectionMode={ConnectionMode.Loose}
          connectionRadius={28}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onNodeClick={async (_, node) => {
            setSelectedId(node.id);
            const d = node.data || {};
            // Click an alternate media version → make it the active scene media
            if (d.mediaVersion && !d.isActive && d.mediaUrl && d.sceneId && d.mediaKind) {
              try {
                const patched = await selectSceneMediaVersion({
                  mode,
                  projectId,
                  sceneId: d.sceneId,
                  kind: d.mediaKind,
                  url: d.mediaUrl,
                });
                if (mode === 'narration' && patched && onScenesChange) {
                  onScenesChange(mergePatchedScene(scenes, patched, mode));
                } else if (onReload) {
                  await onReload();
                }
                toast.success(
                  d.mediaKind === 'image'
                    ? 'Image version selected'
                    : d.mediaKind === 'audio'
                      ? 'Audio version selected'
                      : 'Video version selected',
                );
              } catch (err) {
                toast.error(formatApiError(err, 'Could not switch version'));
              }
            }
          }}
          onPaneClick={() => setSelectedId(null)}
          onNodeDragStop={(_, _node, currentNodes) => {
            persistLayout(currentNodes);
          }}
          nodeTypes={WORKFLOW_NODE_TYPES}
          defaultEdgeOptions={{
            type: 'default',
            animated: true,
            style: { stroke: '#62FAE3', strokeWidth: 1.5 },
          }}
          connectionLineType={ConnectionLineType.Bezier}
          connectionLineStyle={{ stroke: '#62FAE3', strokeWidth: 1.5 }}
          fitView
          fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
          selectionOnDrag
          panOnScroll
          edgesReconnectable
          className="bg-[#070b0e]"
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#1e272e" gap={22} size={1} />
          <Controls className="!bg-[#11171d] !border-white/10 !shadow-xl [&>button]:!bg-[#11171d] [&>button]:!border-white/10 [&>button]:!fill-slate-300" />
          <MiniMap
            className="!bg-[#11171d] !border-white/10"
            nodeColor={(n) => {
              const k = n.data?.kind;
              if (k === 'video') return '#2DD4BF';
              if (k === 'image') return '#A78BFA';
              if (k === 'text') return '#93C5FD';
              if (k === 'upscaler') return '#FBBF24';
              return '#F472B6';
            }}
            maskColor="rgba(0,0,0,0.55)"
          />
          <Panel position="top-right" className="flex gap-2">
            <button
              type="button"
              onClick={() => onReload?.()}
              className="h-8 px-2.5 rounded-lg border border-white/10 bg-[#11171d] text-[11px] text-slate-300 hover:text-white inline-flex items-center gap-1.5 cursor-pointer"
            >
              <RefreshCw className="w-3 h-3" />
              Reload story
            </button>
            <button
              type="button"
              onClick={resetToStory}
              className="h-8 px-2.5 rounded-lg border border-white/10 bg-[#11171d] text-[11px] text-slate-300 hover:text-white cursor-pointer"
            >
              Reset layout
            </button>
          </Panel>
          <Panel position="top-left" className="!m-3">
            <div className="rounded-lg border border-white/10 bg-[#11171d]/90 px-2.5 py-1.5 text-[10px] text-slate-400 max-w-[300px]">
              <span className="text-[#62FAE3] font-semibold">Media versions</span>
              {' · '}
              Selected takes are highlighted — click an Alt node to activate it
            </div>
          </Panel>
        </ReactFlow>
      </div>

      <aside className="w-64 shrink-0 border-l border-white/10 bg-[#0b1015]/95 flex flex-col">
        <div className="px-3 py-2.5 border-b border-white/10">
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#9CA3AF]">Inspector</p>
        </div>
        {selected ? (
          <div className="p-3 space-y-3 flex-1 overflow-y-auto">
            <div>
              <p className="text-[11px] font-semibold text-white">{selected.data?.label}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">
                {selected.data?.synced
                  ? `Linked to scene · edits update Scene & Editor`
                  : selected.data?.kind}
              </p>
            </div>
            {(selected.data?.kind === 'text' || selected.data?.kind === 'image') &&
            !selected.data?.readOnly &&
            !selected.data?.mediaVersion ? (
              <div className="space-y-1.5">
                <label className="text-[10px] text-slate-400">
                  {selected.data?.field === 'visual' ? 'Visual / image prompt' : 'Script / prompt'}
                </label>
                <textarea
                  value={selected.data?.text || ''}
                  disabled={saving}
                  onChange={(e) => updateSelectedText(e.target.value)}
                  rows={8}
                  className="w-full rounded-lg bg-black/40 border border-white/10 text-[11px] text-slate-200 p-2 focus:outline-none focus:border-[#62FAE3]/50 resize-y min-h-[120px] disabled:opacity-60"
                />
                {saving ? (
                  <p className="text-[10px] text-[#62FAE3] flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Saving to story…
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="text-[11px] text-slate-500 leading-relaxed">
                {selected.data?.readOnly
                  ? 'This node is derived from the story title / preview and is read-only.'
                  : selected.data?.kind === 'video'
                    ? 'Video media comes from the same scene as Scene View and Editor View. Generate or replace it there.'
                    : 'Select a script or image node to edit shared story fields.'}
              </p>
            )}
            {selected.data?.imageUrl ? (
              <img
                src={selected.data.imageUrl}
                alt=""
                className="w-full rounded-lg border border-white/10 object-cover aspect-video"
              />
            ) : null}
          </div>
        ) : (
          <div className="p-4 text-[11px] text-slate-500 leading-relaxed">
            Select a node. Script and visual nodes write back to the same scenes used by Scene View
            and Editor View.
          </div>
        )}
      </aside>
    </div>
  );
}

export function NodeWorkflowCanvas({
  mode,
  projectId,
  title,
  scenes,
  onScenesChange,
  loading,
  onReload,
}) {
  return (
    <ReactFlowProvider>
      <WorkflowCanvasInner
        mode={mode}
        projectId={projectId}
        title={title}
        scenes={scenes}
        onScenesChange={onScenesChange}
        loading={loading}
        onReload={onReload}
      />
    </ReactFlowProvider>
  );
}

export default NodeWorkflowCanvas;
